import { TaggingFlag } from '@/hooks/api/use-save-column-metadata-mutation'
import { EditableFieldName } from './types'

// 👇 Service Configuration

export const SERVICE_CONFIG = {
  SERVICE_NAME: 'em-data-governance',
  DATABASE_NAME: 'em-data-governance-datastore',
} as const

// 👇 Agent Configuration

export const AGENT_IDS = {
  DATA_CATALOG_AGENT: 'em-data-catalog-agent',
  METADATA_AGENT: 'em-metadata-agent',
} as const

// 👇 Enrichment Constants

export const ENRICHMENT_TOOL_IDS = {
  GENERATE_TABLE_DESCRIPTION: 'generateTableDescription',
  ENRICH_METADATA: 'enrichMetadata',
  TAG_GENERATOR: 'tagGenerator',
  DQ_TAG_GENERATOR: 'dqTagGenerator',
  SENSITIVITY_TAG_GENERATOR: 'sensitivityTagGenerator',
  GENERATE_DQ_RULES: 'generateDqRules',
} as const

export const ENRICHMENT_AGENTS = {
  EM_AGENTS: 'emAgents',
  BANKING: 'banking',
  HEALTHCARE: 'healthcare',
  RETAIL: 'retail',
  TELECOMMUNICATIONS: 'telecommunications',
  ENERGY: 'energy',
  PUBLIC: 'public',
} as const

export const ENRICHMENT_METADATA_TYPES = {
  TABLE_DESCRIPTION: 'table_description',
  COLUMN_DESCRIPTION: 'column_description',
  PII_TAGS: 'pii_tags',
  DATA_QUALITY_TAGS: 'data_quality_tags',
  SENSITIVITY_TAGS: 'sensitivity_tags',
  DQ_RULES: 'dq_rules',
} as const

export const ENRICHMENT_TYPES = {
  TABLE: 'table',
  COLUMN: 'column',
} as const

export const ENRICHMENT_FIELD_MAPPINGS = {
  TABLE_DESCRIPTION: ['table_description'],
  COLUMN_METADATA: ['column_description', 'business_definition', 'business_rules'],
  PII_TAGS: ['tagging'],
  DATA_QUALITY_TAGS: ['data_quality_tagging'],
  SENSITIVITY_TAGS: ['sensitivity'],
  DQ_RULES: ['dq_rules'],
} as const

// Type for enrichment field values using constants
export type EnrichmentFieldValue =
  (typeof ENRICHMENT_FIELD_MAPPINGS)[keyof typeof ENRICHMENT_FIELD_MAPPINGS][number]

export const TAGGING_FLAG_PROPERTIES = {
  PII_TAGGED: 'is_pii_tagged',
  DQ_TAGGED: 'is_dq_tagged',
  SENSITIVITY_TAGGED: 'is_sensitivity_tagged',
} as const

export const TAG_CATEGORIES = {
  PII_DG: 'PII_DG',
  DATA_QUALITY_TAGS: 'DATA_QUALITY_TAGS',
  SENSITIVITY_TAGS: 'SENSITIVITY_TAGS',
} as const

// 👇 Data Catalog API Constants

export const DATA_CATALOG_API_TOOL_IDS = {
  FETCH_ALL_SCHEMAS: 'fetchAllSchemas',
  FETCH_ALL_TABLES: 'fetchAllTables',
  FETCH_TABLE_DETAILS: 'fetchTableDetails',
  GET_TABLE_VERSION_HISTORY: 'getTableVersionHistory',
  UPDATE_COLUMN: 'updateColumn',
  UPDATE_TABLE_METADATA: 'updateTableMetadata',
  CLEAR_METADATA: 'clearMetadata',
  COMPUTE_OVERALL_SCORECARD: 'computeOverallScorecard',
} as const

// 👇 UI Constants

export const FIELD_DISPLAY_NAMES: Record<EditableFieldName, string> = {
  column_description: 'description',
  business_definition: 'business definition',
  business_rules: 'business rules',
  table_description: 'table description',
}

// 👇 Types

export type AgentId = (typeof AGENT_IDS)[keyof typeof AGENT_IDS]
export type EnrichmentToolId = (typeof ENRICHMENT_TOOL_IDS)[keyof typeof ENRICHMENT_TOOL_IDS]
export type EnrichmentAgent = (typeof ENRICHMENT_AGENTS)[keyof typeof ENRICHMENT_AGENTS]
export type EnrichmentMetadataType =
  (typeof ENRICHMENT_METADATA_TYPES)[keyof typeof ENRICHMENT_METADATA_TYPES]
export type EnrichmentType = (typeof ENRICHMENT_TYPES)[keyof typeof ENRICHMENT_TYPES]
export type EnrichmentFieldMapping =
  (typeof ENRICHMENT_FIELD_MAPPINGS)[keyof typeof ENRICHMENT_FIELD_MAPPINGS]
export type TaggingFlagProperty =
  (typeof TAGGING_FLAG_PROPERTIES)[keyof typeof TAGGING_FLAG_PROPERTIES]
export type DataCatalogApiToolId =
  (typeof DATA_CATALOG_API_TOOL_IDS)[keyof typeof DATA_CATALOG_API_TOOL_IDS]
export type TagCategory = (typeof TAG_CATEGORIES)[keyof typeof TAG_CATEGORIES]

// 👇 Helper Functions

/**
 * Determines the appropriate tagging flag based on selected metadata
 * @param selectedMetadata - Array of selected metadata types (should contain only one item)
 * @returns TaggingFlag object or undefined if no match found
 */
export const getTaggingFlag = (selectedMetadata?: string[]): TaggingFlag | undefined => {
  if (!selectedMetadata || selectedMetadata.length === 0) {
    return undefined
  }

  const metadataToTaggingFlag: Record<string, TaggingFlagProperty> = {
    [ENRICHMENT_METADATA_TYPES.PII_TAGS]: TAGGING_FLAG_PROPERTIES.PII_TAGGED,
    [ENRICHMENT_METADATA_TYPES.DATA_QUALITY_TAGS]: TAGGING_FLAG_PROPERTIES.DQ_TAGGED,
    [ENRICHMENT_METADATA_TYPES.SENSITIVITY_TAGS]: TAGGING_FLAG_PROPERTIES.SENSITIVITY_TAGGED,
  }

  const metadata = selectedMetadata[0]
  const propertyName = metadataToTaggingFlag[metadata]

  if (propertyName) {
    return {
      property_name: propertyName,
      property_value: 1,
    }
  }

  return undefined
}
